// lib/studio-layer-webgl-raster.ts

// Texels per device pixel, on either axis, above which the box average reads a coarser mip level: a device pixel
// then reads at most (bound + 1)^2 texels. Mip levels are 2x2 box averages, so an overlap-weighted box over a coarser
// level stays close to the level-0 box. An integer, because the shader's loop bound is derived from it.
// Measured GPU time per draw, driven by the level read: bound 2 at most ~1.6 ms, 3-4 (level 0 read longer) up to
// ~3-8 ms, 16 up to ~20 ms (RTX 5070 Ti, 15085x8485 layer, 2482x1198 viewport, 2026-09-28).
export const WEBGL_RASTER_BOX_MAX_FOOTPRINT = 2;

// The tile path paints hsla(hue, 100%, 50%, HEATMAP_OVERLAY_ALPHA) with source-atop over the bitmap.
export const HEATMAP_OVERLAY_ALPHA = 0.5;

type ScreenRect = { left: number; top: number; width: number; height: number };
type ClipRect = { left: number; top: number; right: number; bottom: number };

// Null when no part of the layer survives the viewport and the clipping ancestors. Otherwise the canvas covers the
// whole viewport, snapped outward to device pixels, so its backing store is only reallocated when the viewport
// itself resizes, never while a pan or zoom moves the layer. left/top are the bitmap px (level-0 texel coordinates)
// of the canvas top-left corner; visible is the surviving part, in canvas device px from its top-left corner.
export const resolveWebGLRasterView = (
    layer: ScreenRect,
    viewport: ClipRect,
    clips: readonly ClipRect[],
    bitmapWidth: number,
    bitmapHeight: number,
    devicePixelRatio: number,
) => {
    if (!(layer.width > 0 && layer.height > 0 && bitmapWidth > 0 && bitmapHeight > 0 && devicePixelRatio > 0)) return null;
    let left = Math.max(layer.left, viewport.left);
    let top = Math.max(layer.top, viewport.top);
    let right = Math.min(layer.left + layer.width, viewport.right);
    let bottom = Math.min(layer.top + layer.height, viewport.bottom);
    for (const clip of clips) {
        left = Math.max(left, clip.left);
        top = Math.max(top, clip.top);
        right = Math.min(right, clip.right);
        bottom = Math.min(bottom, clip.bottom);
    }
    if (right <= left || bottom <= top) return null;
    const deviceLeft = Math.floor(viewport.left * devicePixelRatio);
    const deviceTop = Math.floor(viewport.top * devicePixelRatio);
    const deviceWidth = Math.ceil(viewport.right * devicePixelRatio) - deviceLeft;
    const deviceHeight = Math.ceil(viewport.bottom * devicePixelRatio) - deviceTop;
    const visibleLeft = Math.floor(left * devicePixelRatio) - deviceLeft;
    const visibleTop = Math.floor(top * devicePixelRatio) - deviceTop;
    const texelsPerDevicePxX = bitmapWidth / (layer.width * devicePixelRatio);
    const texelsPerDevicePxY = bitmapHeight / (layer.height * devicePixelRatio);
    return {
        left: (deviceLeft - layer.left * devicePixelRatio) * texelsPerDevicePxX,
        top: (deviceTop - layer.top * devicePixelRatio) * texelsPerDevicePxY,
        deviceWidth,
        deviceHeight,
        visible: {
            x: visibleLeft,
            y: visibleTop,
            width: Math.ceil(right * devicePixelRatio) - deviceLeft - visibleLeft,
            height: Math.ceil(bottom * devicePixelRatio) - deviceTop - visibleTop,
        },
        texelsPerDevicePxX,
        texelsPerDevicePxY,
    };
};

// Level 0 up to the bound, else the first level whose halvings bring the footprint within it, clamped to the
// last level, whose single texel covers the whole bitmap. GL floors each side at every halving, hence the
// per-axis scale from level-0 texel coordinates to the chosen level.
export const resolveWebGLRasterSampling = (
    texelsPerDevicePxX: number,
    texelsPerDevicePxY: number,
    bitmapWidth: number,
    bitmapHeight: number,
) => {
    const footprint = Math.max(texelsPerDevicePxX, texelsPerDevicePxY);
    const lastLevel = Math.floor(Math.log2(Math.max(bitmapWidth, bitmapHeight)));
    const level = footprint > WEBGL_RASTER_BOX_MAX_FOOTPRINT
        ? Math.min(lastLevel, Math.ceil(Math.log2(footprint / WEBGL_RASTER_BOX_MAX_FOOTPRINT)))
        : 0;
    const levelWidth = Math.max(1, Math.floor(bitmapWidth / 2 ** level));
    const levelHeight = Math.max(1, Math.floor(bitmapHeight / 2 ** level));
    return { level, levelWidth, levelHeight, scaleX: levelWidth / bitmapWidth, scaleY: levelHeight / bitmapHeight };
};

// hsl(hue, 100%, 50%) as 0..1 RGB, per the CSS Color 4 conversion.
export const resolveHeatmapOverlayRgb = (hue: number): [number, number, number] => {
    const channel = (n: number) => {
        const k = (n + hue / 30) % 12;
        return 0.5 - 0.5 * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    };
    return [channel(0), channel(8), channel(4)];
};

// Under software rendering the box average would run on the CPU: the 2D tiles are the better fallback there.
const CONTEXT_ATTRIBUTES: WebGLContextAttributes = {
    alpha: true,
    premultipliedAlpha: true,
    antialias: false,
    depth: false,
    stencil: false,
    failIfMajorPerformanceCaveat: true,
};

// A footprint within the bound straddles at most bound + 1 texels per axis.
const MAX_TAPS_PER_AXIS = WEBGL_RASTER_BOX_MAX_FOOTPRINT + 1;

const VERTEX_SHADER_SOURCE = `#version 300 es
void main() {
    vec2 corner = vec2(float(gl_VertexID & 1), float(gl_VertexID >> 1));
    gl_Position = vec4(corner * 2.0 - 1.0, 0.0, 1.0);
}`;

// u_origin is the texel coordinate, at u_level, of the canvas top-left corner; u_step the texels per device pixel.
// At level 0, an axis whose footprint fits in one texel takes the texel under the pixel centre, as
// image-rendering: pixelated does; otherwise every texel the footprint covers is averaged, weighted by overlap.
// Outside the bitmap the reads are empty and the pixel is transparent. The heatmap is source-atop over the result.
const FRAGMENT_SHADER_SOURCE = `#version 300 es
precision highp float;
precision highp int;
precision highp sampler2D;
uniform sampler2D u_texture;
uniform int u_level;
uniform ivec2 u_levelSize;
uniform vec2 u_origin;
uniform vec2 u_step;
uniform float u_canvasHeight;
uniform vec4 u_heatmap;
out vec4 outColor;
void main() {
    vec2 device = vec2(gl_FragCoord.x, u_canvasHeight - gl_FragCoord.y);
    vec2 lo = u_origin + (device - 0.5) * u_step;
    vec2 hi = lo + u_step;
    if (u_level == 0) {
        vec2 centre = u_origin + device * u_step;
        if (u_step.x <= 1.0) { lo.x = floor(centre.x); hi.x = lo.x + 1.0; }
        if (u_step.y <= 1.0) { lo.y = floor(centre.y); hi.y = lo.y + 1.0; }
    }
    ivec2 first = max(ivec2(floor(lo)), ivec2(0));
    ivec2 last = min(ivec2(ceil(hi)) - 1, u_levelSize - 1);
    vec4 sum = vec4(0.0);
    for (int j = 0; j < ${MAX_TAPS_PER_AXIS}; j++) {
        int y = first.y + j;
        if (y > last.y) break;
        float weightY = min(hi.y, float(y + 1)) - max(lo.y, float(y));
        for (int i = 0; i < ${MAX_TAPS_PER_AXIS}; i++) {
            int x = first.x + i;
            if (x > last.x) break;
            float weightX = min(hi.x, float(x + 1)) - max(lo.x, float(x));
            sum += texelFetch(u_texture, ivec2(x, y), u_level) * (weightX * weightY);
        }
    }
    vec4 color = sum / ((hi.x - lo.x) * (hi.y - lo.y));
    outColor = vec4(mix(color.rgb, u_heatmap.rgb * color.a, u_heatmap.a), color.a);
}`;

// The canvas spans the whole viewport, past the layer box: the layer's own boxes, not the canvas, take the pointer.
export const CANVAS_BASE_STYLE: Partial<CSSStyleDeclaration> = {
    position: 'absolute',
    left: '0',
    top: '0',
    transformOrigin: '0 0',
    imageRendering: 'pixelated',
    pointerEvents: 'none',
    display: 'none',
};

type WebGLRasterRenderer = {
    holdsOtherSize: (width: number, height: number) => boolean;
    upload: (imageData: ImageData, key: string) => boolean;
    setHeatmapHue: (hue: number | null) => void;
    draw: () => void;
    dispose: () => void;
};

const compileShader = (gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null => {
    const shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    return shader;
};

const linkProgram = (gl: WebGL2RenderingContext, reportBuildFailure: (log: string) => void): WebGLProgram | null => {
    const program = gl.createProgram();
    const vertexShader = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER_SOURCE);
    const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER_SOURCE);
    if (!program || !vertexShader || !fragmentShader) return null;
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    if (gl.getProgramParameter(program, gl.LINK_STATUS)) return program;
    if (!gl.isContextLost()) {
        reportBuildFailure([gl.getShaderInfoLog(vertexShader), gl.getShaderInfoLog(fragmentShader), gl.getProgramInfoLog(program)]
            .filter(Boolean)
            .join(' ')
            .trim());
    }
    gl.deleteProgram(program);
    return null;
};

// The canvas is screen-sized, never bitmap-sized: a <canvas> holding the whole bitmap panned at 21-40 fps depending
// on zoom whatever drew it (2D, CPU, bitmaprenderer), because Chrome's compositor is the cost, while a screen-sized
// WebGL canvas held 60 fps (measured on a 15085x8485 layer).
export const createWebGLRasterRenderer = (
    canvas: HTMLCanvasElement,
    bitmapBox: HTMLElement,
    viewportBox: HTMLElement,
    clipElements: readonly HTMLElement[],
    reportBuildFailure: (log: string) => void,
): WebGLRasterRenderer | null => {
    const gl = canvas.getContext('webgl2', CONTEXT_ATTRIBUTES);
    if (!gl) return null;
    const program = linkProgram(gl, reportBuildFailure);
    const texture = gl.createTexture();
    if (!program || !texture) {
        gl.getExtension('WEBGL_lose_context')?.loseContext();
        return null;
    }
    gl.useProgram(program);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST_MIPMAP_NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    // Honoured for a TexImageSource such as ImageData. The mipmaps and the box average must run on premultiplied
    // values, or transparent pixels bleed their colour into their neighbours.
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    const levelLocation = gl.getUniformLocation(program, 'u_level');
    const levelSizeLocation = gl.getUniformLocation(program, 'u_levelSize');
    const originLocation = gl.getUniformLocation(program, 'u_origin');
    const stepLocation = gl.getUniformLocation(program, 'u_step');
    const canvasHeightLocation = gl.getUniformLocation(program, 'u_canvasHeight');
    const heatmapLocation = gl.getUniformLocation(program, 'u_heatmap');

    let uploadedKey: string | null = null;
    let bitmapWidth = 0;
    let bitmapHeight = 0;
    let heatmap: [number, number, number, number] = [0, 0, 0, 0];
    const writtenStyle: Partial<Record<'display' | 'width' | 'height' | 'transform', string>> = {};
    const setStyle = (property: 'display' | 'width' | 'height' | 'transform', value: string) => {
        if (writtenStyle[property] === value) return;
        writtenStyle[property] = value;
        canvas.style[property] = value;
    };

    return {
        holdsOtherSize: (width, height) => bitmapWidth !== 0 && (bitmapWidth !== width || bitmapHeight !== height),
        upload: (imageData, key) => {
            if (key === uploadedKey) return true;
            if (Math.max(imageData.width, imageData.height) > gl.getParameter(gl.MAX_TEXTURE_SIZE)) return false;
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, imageData);
            gl.generateMipmap(gl.TEXTURE_2D);
            if (gl.getError() !== gl.NO_ERROR) return false;
            uploadedKey = key;
            bitmapWidth = imageData.width;
            bitmapHeight = imageData.height;
            return true;
        },
        setHeatmapHue: (hue) => {
            heatmap = typeof hue === 'number' && Number.isFinite(hue)
                ? [...resolveHeatmapOverlayRgb(hue), HEATMAP_OVERLAY_ALPHA]
                : [0, 0, 0, 0];
        },
        draw: () => {
            if (bitmapWidth === 0 || gl.isContextLost()) return;
            const view = resolveWebGLRasterView(
                bitmapBox.getBoundingClientRect(),
                viewportBox.getBoundingClientRect(),
                clipElements.map((element) => element.getBoundingClientRect()),
                bitmapWidth,
                bitmapHeight,
                window.devicePixelRatio || 1,
            );
            if (!view) {
                setStyle('display', 'none');
                return;
            }
            const sampling = resolveWebGLRasterSampling(view.texelsPerDevicePxX, view.texelsPerDevicePxY, bitmapWidth, bitmapHeight);
            // Sized in device px and placed by transform: layout never runs for a pan, only the compositor.
            setStyle('display', 'block');
            setStyle('width', `${view.deviceWidth}px`);
            setStyle('height', `${view.deviceHeight}px`);
            setStyle('transform', `translate(${view.left}px, ${view.top}px) scale(${view.texelsPerDevicePxX}, ${view.texelsPerDevicePxY})`);
            if (canvas.width !== view.deviceWidth) canvas.width = view.deviceWidth;
            if (canvas.height !== view.deviceHeight) canvas.height = view.deviceHeight;
            gl.viewport(0, 0, view.deviceWidth, view.deviceHeight);
            // Cleared whole first: the buffer is only cleared by compositing, so a second draw in the same frame
            // would leave the first one's pixels outside its own scissor.
            gl.disable(gl.SCISSOR_TEST);
            gl.clear(gl.COLOR_BUFFER_BIT);
            gl.enable(gl.SCISSOR_TEST);
            gl.scissor(view.visible.x, view.deviceHeight - view.visible.y - view.visible.height, view.visible.width, view.visible.height);
            gl.uniform1i(levelLocation, sampling.level);
            gl.uniform2i(levelSizeLocation, sampling.levelWidth, sampling.levelHeight);
            gl.uniform2f(originLocation, view.left * sampling.scaleX, view.top * sampling.scaleY);
            gl.uniform2f(stepLocation, view.texelsPerDevicePxX * sampling.scaleX, view.texelsPerDevicePxY * sampling.scaleY);
            gl.uniform1f(canvasHeightLocation, view.deviceHeight);
            gl.uniform4f(heatmapLocation, heatmap[0], heatmap[1], heatmap[2], heatmap[3]);
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        },
        dispose: () => {
            gl.deleteTexture(texture);
            gl.deleteProgram(program);
            gl.getExtension('WEBGL_lose_context')?.loseContext();
        },
    };
};

export const collectClipElements = (host: HTMLElement, viewportElement: HTMLElement): HTMLElement[] => {
    const clipElements: HTMLElement[] = [];
    for (let element = host.parentElement; element && element !== viewportElement; element = element.parentElement) {
        const style = getComputedStyle(element);
        if (style.overflowX !== 'visible' || style.overflowY !== 'visible') clipElements.push(element);
    }
    return clipElements;
};
